import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import * as Sentry from '@sentry/react';
import type { ImageType, ImageView } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosAssetsImagesImagesImageViewFields,
  type SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetData,
  type SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetError,
  type SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetResponse,
  searchImagesV1PortfoliosPortfolioIdAssetsImagesGet,
} from '../../generated';

export type SearchImagesQueryVariables = SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetData['path'] &
  SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetData['query'];

export type SearchImagesQuery = {
  data: SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchImagesQuery = <
  TData = SearchImagesQuery,
  TError = SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetError,
>(
  {
    portfolio_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
  }: SearchImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchImagesQuery, TError, TData>({
    queryKey: [
      'SearchImages',
      {
        portfolio_id,
        limit,
        offset,
        sort_by,
        sort_direction,
        parent_id,
        source,
        exclude_source,
        image_ids,
        product_category_id,
        image_type,
      },
    ],
    queryFn: async () => {
      const { data, response } = await searchImagesV1PortfoliosPortfolioIdAssetsImagesGet({
        path: { portfolio_id },
        query: {
          limit,
          offset,
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
          sort_direction,
          parent_id: parent_id as string[] | undefined,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          product_category_id: product_category_id as string[] | undefined,
          image_type: image_type as ImageType,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchImagesQuery.getKey = ({
  portfolio_id,
  limit,
  offset,
  sort_by,
  sort_direction,
  parent_id,
  source,
  exclude_source,
  image_ids,
  product_category_id,
  image_type,
}: SearchImagesQueryVariables) => [
  'SearchImages',
  {
    portfolio_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
  },
];

useSearchImagesQuery.fetcher = (
  {
    portfolio_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    product_category_id,
    image_type,
  }: SearchImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchImagesV1PortfoliosPortfolioIdAssetsImagesGet({
      path: { portfolio_id },
      query: {
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
        sort_direction,
        parent_id: parent_id as string[] | undefined,
        source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        product_category_id: product_category_id as string[] | null | undefined,
        image_type: image_type as ImageType,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchImagesInfiniteQueryVariables = Omit<SearchImagesQueryVariables, 'offset'>;

export const useSearchImagesInfiniteQuery = <
  TData = SearchImagesQuery,
  TError = SearchImagesV1PortfoliosPortfolioIdAssetsImagesGetError,
>(
  {
    portfolio_id,
    limit,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
  }: SearchImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchImagesQuery, TError, TData>({
    queryKey: [
      'SearchImagesInfinite',
      {
        portfolio_id,
        limit,
        sort_by,
        sort_direction,
        parent_id,
        source,
        exclude_source,
        image_ids,
        product_category_id,
        image_type,
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await searchImagesV1PortfoliosPortfolioIdAssetsImagesGet({
        path: { portfolio_id },
        query: {
          limit,
          offset: pageParam as number,
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
          sort_direction,
          parent_id: parent_id as string[] | undefined,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          image_ids: image_ids as string[] | undefined,
          product_category_id: product_category_id as string[] | undefined,
          image_type: image_type as ImageType,
        },
        throwOnError: true,
      });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: (query: Query<SearchImagesQuery, TError, InfiniteData<SearchImagesQuery, unknown>>) => {
      const maxRetries = typeof options?.retry === 'number' ? options.retry : 100;
      const maxRetriesReached = query.state.dataUpdateCount >= maxRetries;

      // Check all pages for images without scores or URLs
      const allImages = query.state.data?.pages?.flatMap((page: SearchImagesQuery) => page.data) || [];
      const imagesDoNotHaveVizitScore = allImages.some((image: ImageView) => image.vizit_score === null);
      const imagesDoNotHaveUrl = allImages.some(
        (image: ImageView) => image.url === null || !image.url.includes('/images'),
      );

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && !maxRetriesReached) {
        return 1000 * (query.state.dataUpdateCount + 1);
      }

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && maxRetriesReached) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(['GET', '/v1/portfolios/{portfolio_id}/assets/images', 'polling_timeout']);

          Sentry.captureException(new Error('Max retries reached for search images query.'), {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Asset,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetPolling,
            },
            extra: {
              portfolio_id,
              retry_count: query.state.dataUpdateCount,
              max_count: maxRetries,
              query_variables: {
                portfolio_id,
                limit,
                sort_by,
                sort_direction,
                parent_id,
                source,
                exclude_source,
                image_ids,
                product_category_id,
                image_type,
              },
            },
          });
        });
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchImagesInfiniteQuery.getKey = ({
  portfolio_id,
  limit,
  sort_by,
  sort_direction,
  parent_id,
  source,
  exclude_source,
  image_ids,
  product_category_id,
  image_type,
}: SearchImagesInfiniteQueryVariables) => [
  'SearchImagesInfinite',
  {
    portfolio_id,
    limit,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
  },
];

useSearchImagesInfiniteQuery.fetcher = (
  {
    portfolio_id,
    limit,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
  }: SearchImagesInfiniteQueryVariables,
  offset = 0,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchImagesV1PortfoliosPortfolioIdAssetsImagesGet({
      path: { portfolio_id },
      query: {
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
        sort_direction,
        parent_id: parent_id as string[] | undefined,
        source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        image_ids: image_ids as string[] | undefined,
        product_category_id: product_category_id as string[] | undefined,
        image_type: image_type as ImageType,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
